import * as THREE from "three/webgpu";
import {BehaviorSubject, type Observable} from 'rxjs';
import {
    SketchEllipseControlPointFactory,
    SketchEllipseObject,
    SketchLineObject,
    SketchParabolaObject,
} from '../mesh/sketch';
import type {SketchPointObject} from '../mesh/sketch/SketchPointObject';
import {SketchDrawingHandler, SketchDrawingMouseDownPayload} from './SketchDrawingHandler';
import type {SketchDrawingManager} from '../manager/SketchDrawingManager';
import {SketchDrawingTool} from './DrawingSharedTypes';
import {normalizeAngle} from '../tool/normalizeAngle';
import {
    isDashDotLIneNodeMaterial,
    LineMaterialFactory,
    ScreenSpaceDashedLineNodeMaterial,
    syncScreenSpaceDashedLineGeometry,
} from '../material';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../sketch/config/SketchGeometryToleranceConfig';


/**
 * 椭圆绘制命令枚举。
 */
export enum SketchEllipseCommand {
    /** 椭圆命令。 */
    Ellipse = 'sketch-ellipse',
    /** 部分椭圆命令。 */
    Partial = 'sketch-ellipse-partial',
    /** 抛物线命令。 */
    Parabola = 'sketch-ellipse-parabola',
    /** 圆锥命令。 */
    Conic = 'sketch-ellipse-conic',
}

/**
 * 判断命令是否为椭圆绘制命令。
 * @param command 命令字符串。
 * @returns 是否为椭圆绘制命令。
 */
export function isSketchEllipseCommand(command: string): command is SketchEllipseCommand {
    return Object.values(SketchEllipseCommand).includes(command as SketchEllipseCommand);
}

/**
 * 椭圆绘制提示构建参数。
 */
interface SketchEllipseHintInput {
    /** 当前绘制模式。 */
    mode: SketchEllipseCommand;
    /** 当前已落点数量。 */
    pointCount: number;
    /** 预览长轴半径。 */
    radiusX?: number;
    /** 预览短轴半径。 */
    radiusY?: number;
}

/**
 * 部分椭圆弧段求解结果。
 * 说明：
 * 1. 预览与最终实体必须使用同一份起止角与方向；
 * 2. 避免“预览按逆时针长弧、最终实体按顺时针短弧”的方向分裂。
 */
interface PartialEllipseArcState {
    /** 椭圆圆心局部坐标。 */
    localCenter: THREE.Vector2;
    /** 椭圆长半轴半径。 */
    radiusX: number;
    /** 椭圆短半轴半径。 */
    radiusY: number;
    /** 椭圆整体旋转角。 */
    rotation: number;
    /** 弧起始参数角。 */
    startAngle: number;
    /** 弧终止参数角。 */
    endAngle: number;
    /** 是否按顺时针方向取弧。 */
    clockwise: boolean;
    /** 弧扫掠角。 */
    arcAngle: number;
    /** 回贴到椭圆上的弧起点。 */
    startPoint: THREE.Vector3;
    /** 回贴到椭圆上的弧终点。 */
    endPoint: THREE.Vector3;
}

/**
 * 构建椭圆绘制提示文案。
 * @param input 提示构建参数。
 * @returns 绘制提示文案。
 */
function buildSketchEllipseDrawingHint(input: SketchEllipseHintInput): string {
    const { mode, pointCount, radiusX, radiusY } = input;
    let baseHint = '步骤 1：点击中心 → 步骤 2：点击长轴端点 → 步骤 3：点击短轴方向点';

    if (mode === SketchEllipseCommand.Partial) {
        if (pointCount === 0) {
            baseHint = '步骤 1/4：点击中心点';
        } else if (pointCount === 1) {
            baseHint = '步骤 2/4：点击长轴端点';
        } else if (pointCount === 2) {
            baseHint = '步骤 3/4：点击短轴方向点（投影为弧起点）';
        } else if (pointCount === 3) {
            baseHint = '步骤 4/4：点击弧终点';
        }
    } else if (mode === SketchEllipseCommand.Parabola) {
        if (pointCount === 0) {
            baseHint = '步骤 1/4：点击第一个定位点';
        } else if (pointCount === 1) {
            baseHint = '步骤 2/4：点击第二个定位点';
        } else if (pointCount === 2) {
            baseHint = '步骤 3/4：在抛物线上选择起点';
        } else if (pointCount === 3) {
            baseHint = '步骤 4/4：在抛物线上选择终点';
        }
    } else if (pointCount === 0) {
        baseHint = '步骤 1/3：点击中心点';
    } else if (pointCount === 1) {
        baseHint = '步骤 2/3：点击长轴端点';
    } else if (pointCount === 2) {
        baseHint = '步骤 3/3：点击短轴方向（自动垂直于长轴）';
    }

    let extraInfo = '';
    if (Number.isFinite(radiusX)) {
        if (pointCount === 1) {
            extraInfo = ` - 长轴: ${Number(radiusX).toFixed(2)}`;
        } else if (pointCount >= 2 && Number.isFinite(radiusY)) {
            extraInfo = ` - 长轴: ${Number(radiusX).toFixed(2)}, 短轴: ${Number(radiusY).toFixed(2)}`;
        }
    }

    return `${baseHint}${extraInfo} | ESC取消`;
}



/** 椭圆预览颜色。 */
const ELLIPSE_PREVIEW_COLOR = 0x53a9ff;

/** 椭圆处理器屏幕空间虚线段长度。 */
const ELLIPSE_PREVIEW_DASH_SIZE = 7;
/** 椭圆处理器屏幕空间虚线间隔长度。 */
const ELLIPSE_PREVIEW_GAP_SIZE = 4;
/** 椭圆处理器预览透明度。 */
const ELLIPSE_PREVIEW_OPACITY = 0.8;
/** 椭圆第一阶段长轴引导外轮廓分段数。 */
const ELLIPSE_MAJOR_AXIS_GUIDE_SEGMENTS = 128;
/** 抛物线绘制预览采样段数。 */
const PARABOLA_PREVIEW_SEGMENTS = 96;
/** 抛物线定义阶段相对焦距的对称预览范围倍率。 */
const PARABOLA_PREVIEW_EXTENT_FACTOR = 3;

/**
 * 创建椭圆处理器屏幕空间虚线预览材质。
 * 说明：
 * 1. 椭圆与抛物线的定义引导属于临时交互对象，因此固定使用虚线；
 * 2. 屏幕空间节奏可避免 WebGPU 下世界单位过小导致虚线看起来像实线。
 * @param points 当前折线点列。
 * @param materialName 材质调试名称。
 * @returns 屏幕空间虚线材质。
 */
function createEllipseHandlerDashedPreviewMaterial(
    points: THREE.Vector3[],
    materialName: string = '椭圆处理器预览-屏幕空间虚线材质',
): ScreenSpaceDashedLineNodeMaterial {
    const material = new ScreenSpaceDashedLineNodeMaterial({
        color: ELLIPSE_PREVIEW_COLOR,
        linewidth: 1,
        dashSize: ELLIPSE_PREVIEW_DASH_SIZE,
        gapSize: ELLIPSE_PREVIEW_GAP_SIZE,
        opacity: ELLIPSE_PREVIEW_OPACITY,
        transparent: true,
        points,
    });
    Reflect.set(material, 'name', materialName);
    return material;
}


/**
 * 草图椭圆绘制处理器。
 * 说明：
 * 1. 统一承接椭圆工具鼠标按下绘制流程；
 * 2. 支持普通椭圆/部分椭圆/抛物线三种模式点位修正与完成创建；
 * 3. 达到所需点数后立即创建实体并清理绘制状态。
 */
export class SketchEllipseDrawingHandler extends SketchDrawingHandler {
    /** 当前处理器负责的工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.Ellipse;
    /** 椭圆工具选项。 */
    public ellipseOptions: EllipseOptions = {
        mode: SketchEllipseCommand.Ellipse,
        forConstruction: false,
        addConstructionLines: false,
    };
    /** 椭圆参数。 */
    public ellipseParameters: EllipseParameters | null = null;

    /** 每次椭圆绘制点击命中的来源点对象。 */
    private drawingPointTargets: Array<SketchPointObject | null> = [];

    /** 椭圆参数变更流。 */
    private readonly ellipseParametersSubject: BehaviorSubject<EllipseParameters | null> = new BehaviorSubject<EllipseParameters | null>(null);

    /** 椭圆第一阶段长轴引导外轮廓预览对象缓存。 */
    private ellipseMajorAxisGuideCirclePreview: THREE.Line | null = null;
    /** 椭圆第一阶段长轴半径引导线预览对象缓存。 */
    private ellipseMajorAxisGuideLinePreview: THREE.Line | null = null;
    /** 椭圆第一阶段中心点局部坐标复用对象。 */
    private readonly ellipseMajorAxisGuideLocalCenter = new THREE.Vector2();
    /** 椭圆第一阶段鼠标点局部坐标复用对象。 */
    private readonly ellipseMajorAxisGuideLocalCurrent = new THREE.Vector2();
    /** 椭圆第一阶段外轮廓采样点复用数组。 */
    private readonly ellipseMajorAxisGuideCirclePoints = Array.from(
        {length: ELLIPSE_MAJOR_AXIS_GUIDE_SEGMENTS + 1},
        () => new THREE.Vector3(),
    );
    /** 椭圆第一阶段半径引导线采样点复用数组。 */
    private readonly ellipseMajorAxisGuideLinePoints = [
        new THREE.Vector3(),
        new THREE.Vector3(),
    ];

    /** 椭圆第二阶段外轮廓预览对象缓存。 */
    private ellipsePreviewCurve: THREE.Line | null = null;
    /** 椭圆第二阶段外轮廓是否按构造线样式渲染。 */
    private ellipsePreviewIsConstruction: boolean | null = null;
    /** 椭圆第二阶段是否启用了长短轴构造线预览。 */
    private ellipsePreviewHasConstructionLines: boolean | null = null;
    /** 椭圆长轴构造线预览对象缓存。 */
    private ellipseMajorConstructionLinePreview: THREE.Line | null = null;
    /** 椭圆短轴构造线预览对象缓存。 */
    private ellipseMinorConstructionLinePreview: THREE.Line | null = null;


    /** 抛物线预览外轮廓缓存引用（仅持有引用，生命周期由 previewCircle 统一管理）。 */
    private parabolaPreviewCurve: THREE.Line | null = null;
    /** 抛物线第一阶段焦点到临时顶点的引导线缓存引用。 */
    private parabolaDefinitionGuideLinePreview: THREE.Line | null = null;
    /** 抛物线第一阶段焦点引导线采样点复用数组。 */
    private readonly parabolaDefinitionGuideLinePoints = [
        new THREE.Vector3(),
        new THREE.Vector3(),
    ];


    /** 椭圆长轴构造线预览点复用数组。 */
    private readonly ellipseMajorConstructionPreviewPoints = [
        new THREE.Vector3(),
        new THREE.Vector3(),
    ];
    /** 椭圆短轴构造线预览点复用数组。 */
    private readonly ellipseMinorConstructionPreviewPoints = [
        new THREE.Vector3(),
        new THREE.Vector3(),
    ];


    /** 部分椭圆的顺逆时针标记。 */
    public partialEllipseClockwise: boolean | null = null;
    /** 部分椭圆的累积终止角度。 */
    private partialEllipseEndAngle: number | null = null;
    /** 部分椭圆上一次归一化终角。 */
    private partialEllipseLastEndAngleNorm: number | null = null;


    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    constructor(sketchDrawingManager: SketchDrawingManager) {
        super(sketchDrawingManager);
    }

    /**
     * 监听椭圆参数变更（创建/重置时推送）。
     * @returns 椭圆参数只读流。
     */
    public observeEllipseParameters(): Observable<EllipseParameters | null> {
        return this.ellipseParametersSubject.asObservable();
    }

    
    getRequiredPointCount(): number {
        if (this.ellipseOptions.mode === SketchEllipseCommand.Partial) {
            return 4;
        }
        if (this.ellipseOptions.mode === SketchEllipseCommand.Parabola) {
            return 4;
        }
        return 3;
    }

    
    get closable(): boolean {
        return false;
    }

    
    resetState(): void {
        this.drawingPoints = [];

        this.drawingPointTargets = [];


        this.clearEllipseMajorAxisGuidePreviewCache();

        this.ellipseParameters = null;
        this.ellipseParametersSubject.next(null);
        this.resetPartialEllipseDirection();
    }


    /**
     * 获取椭圆工具绘制提示。
     * @returns 提示文本。
     */
    getDrawingHint(): string {
        const pointCount = this.drawingPoints.length;
        let radiusX: number | undefined;
        let radiusY: number | undefined;
        if (this.ellipseOptions.mode !== SketchEllipseCommand.Parabola && pointCount > 0 && this.sketchDrawingManager.getPreviewPoint()) {
            const center = this.drawingPoints[0];
            const axisPoint1 = pointCount >= 2 ? this.drawingPoints[1] : this.sketchDrawingManager.getPreviewPoint();
            const axisPoint2 = pointCount >= 3 ? this.drawingPoints[2] : this.sketchDrawingManager.getPreviewPoint();
            const definition = this.resolveEllipseDefinition(center, axisPoint1, axisPoint2);
            if (definition) {
                radiusX = definition.radiusX;
                radiusY = definition.radiusY;
            }
        }

        return buildSketchEllipseDrawingHint({
            mode: this.ellipseOptions.mode,
            pointCount,
            radiusX,
            radiusY,
        });
    }


    /**
     * 处理椭圆工具预览更新。

     * @param currentPoint 当前预览点。
     */
    updateEllipsePreviewByHandler(currentPoint: THREE.Vector3): void {
        if (this.drawingPoints.length === 0) {
            return;
        }
        const firstPoint = this.drawingPoints[0];
        if (this.ellipseOptions.mode === SketchEllipseCommand.Partial) {
            if (this.drawingPoints.length === 1) {

                this.createEllipseMajorAxisGuidePreview(firstPoint, currentPoint);

                return;
            }
            if (this.drawingPoints.length === 2) {
                this.createEllipsePreviewInHandler(firstPoint, this.drawingPoints[1], currentPoint);
                return;
            }
            if (this.drawingPoints.length === 3) {
                this.createPartialEllipseArcPreviewInHandler(
                    firstPoint,
                    this.drawingPoints[1],
                    this.drawingPoints[2],
                    currentPoint,
                );
            }
            return;
        }
        if (this.ellipseOptions.mode === SketchEllipseCommand.Parabola) {
            if (this.drawingPoints.length === 1) {
                this.createParabolaDefinitionGuidePreview(firstPoint, currentPoint);
                return;
            }
            if (this.drawingPoints.length === 2) {
                this.createParabolaPreviewInHandler(firstPoint, this.drawingPoints[1], currentPoint);
                return;
            }
            if (this.drawingPoints.length === 3) {
                this.createParabolaSegmentPreviewInHandler(
                    firstPoint,
                    this.drawingPoints[1],
                    this.drawingPoints[2],
                    currentPoint,
                );
            }
            return;
        }
        if (this.drawingPoints.length === 1) {

            this.createEllipseMajorAxisGuidePreview(firstPoint, currentPoint);

            return;
        }
        if (this.drawingPoints.length === 2) {
            this.createEllipsePreviewInHandler(firstPoint, this.drawingPoints[1], currentPoint);
        }
    }


    /**
     * 统一预览更新入口。

     * @param currentPoint 当前预览点。
     */
    updatePreview(currentPoint: THREE.Vector3): void {
        this.updateEllipsePreviewByHandler(currentPoint);
    }


    /**
     * 处理椭圆绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。

     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload): void {
        const resolvedPosition = this.resolveEllipseDrawingPosition(payload);
        const point = this.resolveModeAdjustedPoint(resolvedPosition);
        this.drawingPoints.push(point.clone());

        // 保存捕捉命中的正式点对象，实体创建阶段按最终控制点位置决定是否记录来源。
        this.drawingPointTargets.push(payload.snappedTargetPoint ?? null);

        this.processEllipseDrawingStep();

    }

    /**
     * 解析椭圆绘制当前点击点。
     * @param payload 鼠标按下载荷。
     * @returns 本次点击最终采用的世界坐标。
     */
    private resolveEllipseDrawingPosition(payload: SketchDrawingMouseDownPayload): THREE.Vector3 {
        return payload.snappedPoint ?? this.getDrawingPoint(payload.event);
    }


    /**
     * 获取椭圆绘制所需点数。
     * @param mode 椭圆模式。
     * @returns 所需点数。
     */
    public getEllipsePointCount(mode: EllipseOptions['mode']): number {
        if (mode === SketchEllipseCommand.Partial) {
            return 4;
        }
        if (mode === SketchEllipseCommand.Parabola) {
            return 4;
        }
        return 3;
    }


    /**
     * 解析部分椭圆的归一化起止角和弧度。
     * @param startAngle 起始角度。
     * @param endAngle 终止角度。
     * @param clockwise 是否顺时针。
     * @returns 归一化后的起止角和弧度。
     */
    public resolvePartialEllipseAngles(
        startAngle: number,
        endAngle: number,
        clockwise: boolean
    ): { start: number; end: number; arc: number } {
        const start = normalizeAngle(startAngle);
        const end = normalizeAngle(endAngle);
        let arc = clockwise ? start - end : end - start;
        if (arc < 0) {
            arc += Math.PI * 2;
        }
        return {start, end, arc};
    }

    /**
     * 销毁当前绘制预览并清空椭圆、抛物线预览缓存引用。
     * 说明：各阶段预览会复用对象；一旦进入其他阶段或清理绘制状态，缓存必须同步失效，避免复用已被管理器释放的资源。
     */
    protected override destroyPreview(): void {
        super.destroyPreview();
        this.clearEllipseMajorAxisGuidePreviewCache();

        this.clearEllipseCurvePreviewCache();


        this.clearParabolaPreviewCache();

    }


    /**
     * 重置部分椭圆方向状态。
     */
    public resetPartialEllipseDirection(): void {
        this.partialEllipseClockwise = null;
        this.partialEllipseEndAngle = null;
        this.partialEllipseLastEndAngleNorm = null;
    }


    /**
     * 累积更新部分椭圆的终止角度。
     * @param endAngleNorm 归一化终止角度。
     * @returns 累积后的终止角度。
     */
    public updatePartialEllipseEndAngle(endAngleNorm: number): number {
        if (this.partialEllipseEndAngle === null || this.partialEllipseLastEndAngleNorm === null) {
            this.partialEllipseEndAngle = endAngleNorm;
            this.partialEllipseLastEndAngleNorm = endAngleNorm;
            return endAngleNorm;
        }
        let diff = endAngleNorm - this.partialEllipseLastEndAngleNorm;
        if (diff > Math.PI) {
            diff -= Math.PI * 2;
        } else if (diff < -Math.PI) {
            diff += Math.PI * 2;
        }
        this.partialEllipseEndAngle += diff;
        this.partialEllipseLastEndAngleNorm = endAngleNorm;
        return this.partialEllipseEndAngle;
    }


    /**
     * 从中心和两轴点解析椭圆定义(半径/旋转角)。
     * @param center 中心点。
     * @param axisPoint1 长轴端点。
     * @param axisPoint2 短轴方向点。
     * @returns 椭圆定义，若无效返回null。
     */
    public resolveEllipseDefinition(
        center: THREE.Vector3,
        axisPoint1: THREE.Vector3,
        axisPoint2: THREE.Vector3
    ): {
        localCenter: THREE.Vector2;
        radiusX: number;
        radiusY: number;
        rotation: number;
    } | null {
        const referencePlane = this.referencePlane;
        const localCenterRaw = referencePlane.worldToLocal(center);
        const localCenter = new THREE.Vector2(localCenterRaw.x, localCenterRaw.y);
        const localAxis1 = referencePlane.worldToLocal(axisPoint1);
        const localAxis2 = referencePlane.worldToLocal(axisPoint2);
        const axis1 = {
            x: localAxis1.x - localCenter.x,
            y: localAxis1.y - localCenter.y,
        };
        const axis2 = {
            x: localAxis2.x - localCenter.x,
            y: localAxis2.y - localCenter.y,
        };
        const radiusX = Math.hypot(axis1.x, axis1.y);
        if (radiusX < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return null;
        }
        const rotation = Math.atan2(axis1.y, axis1.x);
        const perpX = -Math.sin(rotation);
        const perpY = Math.cos(rotation);
        const radiusY = Math.abs(axis2.x * perpX + axis2.y * perpY);
        return {
            localCenter,
            radiusX,
            radiusY,
            rotation,
        };
    }


    /**
     * 将世界坐标点转换为椭圆参数角。
     * @param point 世界坐标点。
     * @param localCenter 椭圆局部中心。
     * @param radiusX 长轴半径。
     * @param radiusY 短轴半径。
     * @param rotation 旋转角。
     * @returns 参数角（弧度）。
     */
    public getEllipseAngleFromWorldPoint(
        point: THREE.Vector3,
        localCenter: THREE.Vector2,
        radiusX: number,
        radiusY: number,
        rotation: number,
    ): number {
        const local = this.referencePlane.worldToLocal(point);
        const dx = local.x - localCenter.x;
        const dy = local.y - localCenter.y;
        const cosRot = Math.cos(rotation);
        const sinRot = Math.sin(rotation);
        const alignedX = dx * cosRot + dy * sinRot;
        const alignedY = -dx * sinRot + dy * cosRot;
        const nx = radiusX <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength ? 0 : alignedX / radiusX;
        const ny = radiusY <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength ? 0 : alignedY / radiusY;
        return Math.atan2(ny, nx);
    }


    /**
     * 将椭圆参数角转换为世界坐标点。
     * @param angle 参数角（弧度）。
     * @param localCenter 椭圆局部中心。
     * @param radiusX 长轴半径。
     * @param radiusY 短轴半径。
     * @param rotation 旋转角。
     * @returns 世界坐标点。
     */
    public getEllipseWorldPointFromAngle(
        angle: number,
        localCenter: THREE.Vector2,
        radiusX: number,
        radiusY: number,
        rotation: number,
    ): THREE.Vector3 {
        const target = new THREE.Vector3();
        this.writeEllipseWorldPointFromAngleTo(angle, localCenter, radiusX, radiusY, rotation, target);
        return target;
    }

    /**
     * 按椭圆参数角写入世界坐标点。
     * 说明：
     * 1. 预览路径会高频计算轴端点，使用外部传入的 Vector3 避免重复分配；
     * 2. 计算公式与 `getEllipseWorldPointFromAngle()` 保持一致，保证预览构造线和最终椭圆定义同源。
     * @param angle 椭圆参数角。
     * @param localCenter 椭圆中心局部坐标。
     * @param radiusX 长半轴半径。
     * @param radiusY 短半轴半径。
     * @param rotation 椭圆旋转角。
     * @param target 写入目标世界坐标对象。
     */
    private writeEllipseWorldPointFromAngleTo(
        angle: number,
        localCenter: THREE.Vector2,
        radiusX: number,
        radiusY: number,
        rotation: number,
        target: THREE.Vector3,
    ): void {
        const cosAngle = Math.cos(angle);
        const sinAngle = Math.sin(angle);
        const localX = radiusX * cosAngle;
        const localY = radiusY * sinAngle;
        const cosRot = Math.cos(rotation);
        const sinRot = Math.sin(rotation);
        const rotatedX = localX * cosRot - localY * sinRot;
        const rotatedY = localX * sinRot + localY * cosRot;

        this.referencePlane.localToWorldTo(localCenter.x + rotatedX, localCenter.y + rotatedY, target);
    }


    /**
     * 构建椭圆长/短轴辅助构造线。
     * @param axisPoints 椭圆构造线端点（复用草图控制点）。
     * @param isPreview 是否预览模式。
     * @returns 构造线数组。
     */
    public buildEllipseConstructionLines(
        axisPoints: {
            majorStartPoint: SketchPointObject;
            majorEndPoint: SketchPointObject;
            minorStartPoint: SketchPointObject;
            minorEndPoint: SketchPointObject;
        },
        isPreview: boolean = false
    ): SketchLineObject[] {
        /** 创建单条构造线。 */
        const createLine = (startPoint: SketchPointObject, endPoint: SketchPointObject): SketchLineObject => {
            const lineMaterial = isPreview
                ? LineMaterialFactory.getPreviewDashedMaterial().clone()
                : this.createSketchLineMaterial(true, false);
            if (isPreview && lineMaterial instanceof THREE.LineDashedMaterial) {
                lineMaterial.color.setHex(ELLIPSE_PREVIEW_COLOR);
                lineMaterial.opacity = 0.8;
                lineMaterial.transparent = true;
            }
            const line = new SketchLineObject({
                startPoint,
                endPoint,
                material: lineMaterial,
                referencePlane: this.referencePlane,
                isConstruction: true,
            });
            if (line.material instanceof THREE.LineDashedMaterial || isDashDotLIneNodeMaterial(line.material)) {
                line.computeLineDistances();
            }
            return line;
        };
        return [
            createLine(axisPoints.majorStartPoint, axisPoints.majorEndPoint),
            createLine(axisPoints.minorStartPoint, axisPoints.minorEndPoint),
        ];
    }


    /**
     * 更新椭圆构造线预览点。
     * 说明：
     * 1. 长轴构造线连接 0° 与 180° 轴端；
     * 2. 短轴构造线连接 90° 与 270° 轴端；
     * 3. 两条线都直接来自当前椭圆定义，避免预览与最终创建构造线脱节。
     * @param localCenter 椭圆中心局部坐标。
     * @param radiusX 长半轴半径。
     * @param radiusY 短半轴半径。
     * @param rotation 椭圆旋转角。
     */
    private updateEllipseConstructionPreviewPoints(
        localCenter: THREE.Vector2,
        radiusX: number,
        radiusY: number,
        rotation: number,
    ): void {
        // 轴端点在鼠标移动中持续更新，因此写入复用数组，减少临时对象。
        this.writeEllipseWorldPointFromAngleTo(
            0,
            localCenter,
            radiusX,
            radiusY,
            rotation,
            this.ellipseMajorConstructionPreviewPoints[0],
        );
        this.writeEllipseWorldPointFromAngleTo(
            Math.PI,
            localCenter,
            radiusX,
            radiusY,
            rotation,
            this.ellipseMajorConstructionPreviewPoints[1],
        );
        this.writeEllipseWorldPointFromAngleTo(
            Math.PI / 2,
            localCenter,
            radiusX,
            radiusY,
            rotation,
            this.ellipseMinorConstructionPreviewPoints[0],
        );
        this.writeEllipseWorldPointFromAngleTo(
            (Math.PI * 3) / 2,
            localCenter,
            radiusX,
            radiusY,
            rotation,
            this.ellipseMinorConstructionPreviewPoints[1],
        );
    }

    /**
     * 创建屏幕空间虚线预览对象。
     * 说明：椭圆构造线、椭圆定义引导与抛物线定义引导共享同一底层创建规则，
     * 避免不同绘制阶段分别维护 WebGPU 虚线材质和距离属性同步逻辑。
     * @param points 预览折线点列。
     * @param materialName 材质调试名称。
     * @returns 屏幕空间虚线预览对象。
     */
    private createScreenSpaceDashedPreviewLine(
        points: THREE.Vector3[],
        materialName: string,
    ): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const line = new THREE.Line(geometry, createEllipseHandlerDashedPreviewMaterial(points, materialName));
        // `ScreenSpaceDashedLineNodeMaterial` 依赖距离百分比 attribute，创建后必须立即同步。
        syncScreenSpaceDashedLineGeometry(line);
        return line;
    }

    /**
     * 渲染椭圆构造线预览。
     * 说明：
     * 1. 仅在“添加构造线”开启且椭圆定义有效时显示；
     * 2. 预览对象不创建草图点，不进入正式草图实体集合；
     * 3. 已有长短轴预览对象可复用时只更新几何，避免鼠标移动时重复创建材质和线对象。
     * @param localCenter 椭圆中心局部坐标。
     * @param radiusX 长半轴半径。
     * @param radiusY 短半轴半径。
     * @param rotation 椭圆旋转角。
     */
    private renderEllipseConstructionLinePreview(
        localCenter: THREE.Vector2,
        radiusX: number,
        radiusY: number,
        rotation: number,
    ): void {
        if (!this.ellipseOptions.addConstructionLines) {
            return;
        }

        this.updateEllipseConstructionPreviewPoints(localCenter, radiusX, radiusY, rotation);
        if (
            !this.ellipseMajorConstructionLinePreview
            || !this.ellipseMinorConstructionLinePreview
            || this.sketchDrawingManager.previewConstructionLines[0] !== this.ellipseMajorConstructionLinePreview
            || this.sketchDrawingManager.previewConstructionLines[1] !== this.ellipseMinorConstructionLinePreview
        ) {
            const majorLine = this.createScreenSpaceDashedPreviewLine(
                this.ellipseMajorConstructionPreviewPoints,
                '椭圆长轴构造线预览-屏幕空间虚线材质',
            );
            const minorLine = this.createScreenSpaceDashedPreviewLine(
                this.ellipseMinorConstructionPreviewPoints,
                '椭圆短轴构造线预览-屏幕空间虚线材质',
            );

            this.ellipseMajorConstructionLinePreview = majorLine;
            this.ellipseMinorConstructionLinePreview = minorLine;
            this.sketchDrawingManager.previewConstructionLines = [majorLine, minorLine];
            this.sketchDrawingManager.previewConstructionLines.forEach((line) => {
                this.viewer.addObject(line);
            });
            return;
        }

        // 高频预览路径只改写已有构造线几何，避免每次鼠标移动创建材质与 GPU 资源。
        this.updateScreenSpaceDashedPreviewLine(
            this.ellipseMajorConstructionLinePreview,
            this.ellipseMajorConstructionPreviewPoints,
        );
        this.updateScreenSpaceDashedPreviewLine(
            this.ellipseMinorConstructionLinePreview,
            this.ellipseMinorConstructionPreviewPoints,
        );
    }


    /**
     * 判断椭圆第二阶段预览缓存是否仍可复用。
     * @param isConstruction 本次外轮廓是否按构造线样式渲染。
     * @param hasConstructionLines 本次是否需要长短轴构造线预览。
     * @returns 当前预览对象是否仍可复用。
     */
    private isEllipseCurvePreviewActive(
        isConstruction: boolean,
        hasConstructionLines: boolean,
    ): boolean {
        const isCurveActive = Boolean(
            this.ellipsePreviewCurve
            && this.sketchDrawingManager.previewCircle === this.ellipsePreviewCurve
            && this.ellipsePreviewIsConstruction === isConstruction,
        );
        const isConstructionPreviewActive = hasConstructionLines
            ? Boolean(
                this.ellipseMajorConstructionLinePreview
                && this.ellipseMinorConstructionLinePreview
                && this.sketchDrawingManager.previewConstructionLines[0] === this.ellipseMajorConstructionLinePreview
                && this.sketchDrawingManager.previewConstructionLines[1] === this.ellipseMinorConstructionLinePreview
                && this.ellipsePreviewHasConstructionLines === true,
            )
            : this.ellipseMajorConstructionLinePreview === null
                && this.ellipseMinorConstructionLinePreview === null
                && this.sketchDrawingManager.previewConstructionLines.length === 0
                && this.ellipsePreviewHasConstructionLines === false;

        return isCurveActive && isConstructionPreviewActive;
    }

    /**
     * 创建椭圆第二阶段外轮廓预览对象。
     * @param points 外轮廓折线采样点。
     * @param isConstruction 是否按构造线样式渲染。
     * @returns 椭圆外轮廓预览对象。
     */
    private createEllipseCurvePreviewLine(
        points: THREE.Vector3[],
        isConstruction: boolean,
    ): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = isConstruction
            ? new THREE.LineDashedMaterial({
                color: ELLIPSE_PREVIEW_COLOR,
                linewidth: 1,
                dashSize: 3,
                gapSize: 2,
                opacity: ELLIPSE_PREVIEW_OPACITY,
                transparent: true,
            })
            : new THREE.LineBasicMaterial({
                color: ELLIPSE_PREVIEW_COLOR,
                linewidth: 2,
                opacity: ELLIPSE_PREVIEW_OPACITY,
                transparent: true,
            });
        const previewCurve = new THREE.Line(geometry, material);
        if (material instanceof THREE.LineDashedMaterial) {
            previewCurve.computeLineDistances();
        }
        return previewCurve;
    }

    /**
     * 原地更新椭圆第二阶段外轮廓预览。
     * @param line 椭圆外轮廓预览对象。
     * @param points 新外轮廓折线采样点。
     * @param isConstruction 是否按构造线样式渲染。
     */
    private updateEllipseCurvePreviewLine(
        line: THREE.Line,
        points: THREE.Vector3[],
        isConstruction: boolean,
    ): void {
        this.updateEllipsePreviewLineGeometry(line, points);
        if (isConstruction) {
            line.computeLineDistances();
        }
    }

    /**
     * 渲染椭圆第二阶段外轮廓和可选构造线预览。
     * 说明：
     * 1. 与圆预览一致，首次进入当前形态时创建对象，后续鼠标移动只更新 geometry；
     * 2. 构造线开关或外轮廓线型变化时才清理并重建，避免每帧反复释放和创建 GPU 资源；
     * 3. 该逻辑只服务完整椭圆和部分椭圆，抛物线继续使用通用折线预览。
     * @param points 外轮廓折线采样点。
     * @param localCenter 椭圆中心局部坐标。
     * @param radiusX 长半轴半径。
     * @param radiusY 短半轴半径。
     * @param rotation 椭圆旋转角。
     * @param isConstruction 外轮廓是否按构造线样式渲染。
     */
    private renderEllipseCurvePreview(
        points: THREE.Vector3[],
        localCenter: THREE.Vector2,
        radiusX: number,
        radiusY: number,
        rotation: number,
        isConstruction: boolean,
    ): void {
        if (points.length < 2) {
            this.destroyPreview();
            return;
        }

        const hasConstructionLines = this.ellipseOptions.addConstructionLines;
        if (!this.isEllipseCurvePreviewActive(isConstruction, hasConstructionLines)) {
            this.destroyPreview();
            this.sketchDrawingManager.previewCircle = this.createEllipseCurvePreviewLine(points, isConstruction);
            this.ellipsePreviewCurve = this.sketchDrawingManager.previewCircle;
            this.ellipsePreviewIsConstruction = isConstruction;
            this.ellipsePreviewHasConstructionLines = hasConstructionLines;
            this.viewer.addObject(this.sketchDrawingManager.previewCircle);

            if (hasConstructionLines) {
                this.renderEllipseConstructionLinePreview(localCenter, radiusX, radiusY, rotation);
            }
            return;
        }

        this.updateEllipseCurvePreviewLine(
            this.ellipsePreviewCurve as THREE.Line,
            points,
            isConstruction,
        );
        if (hasConstructionLines) {
            this.renderEllipseConstructionLinePreview(localCenter, radiusX, radiusY, rotation);
        }
    }


    /**
     * 从顶点和焦点解析抛物线的轴方向和参数p。
     * @param vertex 抛物线顶点。
     * @param focus 抛物线焦点。
     * @returns 抛物线定义，若无效返回null。
     */
    public resolveParabolaDefinition(
        vertex: THREE.Vector3,
        focus: THREE.Vector3
    ): {
        vertex: THREE.Vector3;
        axisDir: THREE.Vector3;
        sideDir: THREE.Vector3;
        p: number;
    } | null {
        const axisDir = focus.clone().sub(vertex);
        const normal = this.referencePlane.normal.clone().normalize();
        axisDir.sub(normal.clone().multiplyScalar(axisDir.dot(normal)));
        if (axisDir.lengthSq() < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLengthSquared) {
            return null;
        }
        axisDir.normalize();
        const sideDir = new THREE.Vector3().crossVectors(axisDir, normal).normalize();
        const p = vertex.distanceTo(focus);
        if (!Number.isFinite(p) || p <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return null;
        }
        return {
            vertex,
            axisDir,
            sideDir,
            p,
        };
    }


    /**
     * 将世界点投影到抛物线上。
     * @param point 世界坐标点。
     * @param def 抛物线定义。
     * @returns 投影结果。
     */
    public projectPointToParabola(
        point: THREE.Vector3,
        def: { vertex: THREE.Vector3; axisDir: THREE.Vector3; sideDir: THREE.Vector3; p: number }
    ): { x: number; point: THREE.Vector3 } {
        const relative = point.clone().sub(def.vertex);
        const x = relative.dot(def.sideDir);
        const y = (x * x) / (4 * def.p);
        const world = def.vertex.clone()
            .add(def.sideDir.clone().multiplyScalar(x))
            .add(def.axisDir.clone().multiplyScalar(y));
        return {x, point: world};
    }


    /**
     * 构建抛物线世界坐标点序列。
     * @param def 抛物线定义。
     * @param startX 起始x参数。
     * @param endX 终止x参数。
     * @param segments 采样段数。
     * @returns 世界坐标点数组。
     */
    public buildParabolaPoints(
        def: { vertex: THREE.Vector3; axisDir: THREE.Vector3; sideDir: THREE.Vector3; p: number },
        startX: number,
        endX: number,
        segments = 64
    ): THREE.Vector3[] {
        const points: THREE.Vector3[] = [];
        const fromX = Math.min(startX, endX);
        const toX = Math.max(startX, endX);
        const step = (toX - fromX) / Math.max(segments, 1);
        for (let i = 0; i <= segments; i += 1) {
            const x = fromX + step * i;
            const y = (x * x) / (4 * def.p);
            const world = def.vertex.clone()
                .add(def.sideDir.clone().multiplyScalar(x))
                .add(def.axisDir.clone().multiplyScalar(y));
            points.push(world);
        }
        return points;
    }


    /**
     * 按椭圆模式修正当前点击点。
     * @param point 原始点位。

     * @returns 修正后的点位。
     */
    private resolveModeAdjustedPoint(
        point: THREE.Vector3,
    ): THREE.Vector3 {
        let resolvedPoint = point.clone();

        if (this.ellipseOptions.mode === SketchEllipseCommand.Partial && this.drawingPoints.length >= 3) {
            const center = this.drawingPoints[0];
            const axisPoint1 = this.drawingPoints[1];
            const axisPoint2 = this.drawingPoints[2];
            const def = this.resolveEllipseDefinition(center, axisPoint1, axisPoint2);
            if (def) {
                const angle = this.getEllipseAngleFromWorldPoint(
                    resolvedPoint,
                    def.localCenter,
                    def.radiusX,
                    def.radiusY,
                    def.rotation,
                );
                resolvedPoint = this.getEllipseWorldPointFromAngle(
                    angle,
                    def.localCenter,
                    def.radiusX,
                    def.radiusY,
                    def.rotation,
                );
            }
        }

        if (this.ellipseOptions.mode === SketchEllipseCommand.Parabola && this.drawingPoints.length >= 2) {
            const focus = this.drawingPoints[0];
            const vertex = this.drawingPoints[1];
            const def = this.resolveParabolaDefinition(vertex, focus);
            if (def) {
                resolvedPoint = this.projectPointToParabola(resolvedPoint, def).point;
            }
        }

        return resolvedPoint;
    }

    /**
     * 从已落点和当前终点提示统一求解部分椭圆弧段。
     * @param center 椭圆中心点。
     * @param axisPoint1 长轴端点。
     * @param axisPoint2 短轴方向点，同时作为部分椭圆起点投影来源。
     * @param endHint 终点提示点。
     * @param shouldTrackPointerDirection 是否把本次终点纳入方向累计。
     * @returns 部分椭圆弧段求解结果；无效椭圆返回 null。
     */
    private resolvePartialEllipseArcState(
        center: THREE.Vector3,
        axisPoint1: THREE.Vector3,
        axisPoint2: THREE.Vector3,
        endHint: THREE.Vector3,
        shouldTrackPointerDirection: boolean,
    ): PartialEllipseArcState | null {
        const definition = this.resolveEllipseDefinition(center, axisPoint1, axisPoint2);
        if (!definition) {
            return null;
        }

        const {localCenter, radiusX, radiusY, rotation} = definition;

        // 短轴方向点的原始鼠标位置要先投影到椭圆上，再作为弧起点；否则起点会被压成上/下短轴端。
        const startAngleRaw = this.getEllipseAngleFromWorldPoint(
            axisPoint2,
            localCenter,
            radiusX,
            radiusY,
            rotation,
        );

        const endAngleRaw = this.getEllipseAngleFromWorldPoint(
            endHint,
            localCenter,
            radiusX,
            radiusY,
            rotation,
        );
        const endAngleNorm = normalizeAngle(endAngleRaw);
        const startAngleNorm = normalizeAngle(startAngleRaw);

        /**
         * 第四步拖动定义的是部分椭圆范围，必须在预览阶段累计指针绕行方向。
         * 若没有预览事件，最终点击时仍可用单次终点角完成退化求解。
         */
        const endAngleUnwrapped = shouldTrackPointerDirection
            ? this.updatePartialEllipseEndAngle(endAngleNorm)
            : (this.partialEllipseEndAngle ?? this.updatePartialEllipseEndAngle(endAngleNorm));
        const deltaUnwrapped = endAngleUnwrapped - startAngleNorm;
        const clockwise = deltaUnwrapped < 0;
        this.partialEllipseClockwise = clockwise;

        const normalized = this.resolvePartialEllipseAngles(startAngleRaw, endAngleRaw, clockwise);
        const startPoint = this.getEllipseWorldPointFromAngle(
            normalized.start,
            localCenter,
            radiusX,
            radiusY,
            rotation,
        );
        const endPoint = this.getEllipseWorldPointFromAngle(
            normalized.end,
            localCenter,
            radiusX,
            radiusY,
            rotation,
        );

        return {
            localCenter,
            radiusX,
            radiusY,
            rotation,
            startAngle: normalized.start,
            endAngle: normalized.end,
            clockwise,
            arcAngle: normalized.arc,
            startPoint,
            endPoint,
        };
    }

    /**
     * 执行椭圆绘制单步提交。

     */
    private processEllipseDrawingStep(): void {
        if (this.ellipseOptions.mode === SketchEllipseCommand.Partial) {
            if (this.drawingPoints.length === this.getEllipsePointCount(SketchEllipseCommand.Partial)) {
                this.createPartialEllipseEntity();
                this.drawingPoints = [];

                this.drawingPointTargets = [];

                this.destroyPreview();
                this.resetPartialEllipseDirection();
            } else if (this.drawingPoints.length === 3) {
                this.destroyPreview();
                this.resetPartialEllipseDirection();
            }
            return;
        }

        if (this.ellipseOptions.mode === SketchEllipseCommand.Parabola) {
            if (this.drawingPoints.length === this.getEllipsePointCount(SketchEllipseCommand.Parabola)) {
                this.createParabolaEntity();
                this.drawingPoints = [];

                this.drawingPointTargets = [];

                this.destroyPreview();
            }
            return;
        }

        if (this.drawingPoints.length === this.getEllipsePointCount(SketchEllipseCommand.Ellipse)) {
            this.createEllipseEntity();
            this.drawingPoints = [];

            this.drawingPointTargets = [];

            this.destroyPreview();
        }
    }

    /**
     * 在处理器内创建完整椭圆实体。

     */
    private createEllipseEntity(): void {
        if (this.drawingPoints.length < 3 ) {
            return;
        }

        const center = this.drawingPoints[0];
        const axisPoint1 = this.drawingPoints[1];
        const axisPoint2 = this.drawingPoints[2];
        const definition = this.resolveEllipseDefinition(center, axisPoint1, axisPoint2);
        if (!definition) {
            return;
        }

        const {localCenter, radiusX, radiusY, rotation} = definition;
        const isConstruction = this.ellipseOptions.forConstruction;
        const material = this.createSketchLineMaterial(isConstruction, false);

        const controlPointFactory = new SketchEllipseControlPointFactory(this.referencePlane);

        const reusedControlPoints = new Set<SketchPointObject>();
        const centerControlPoint = this.resolveReusableEllipseControlPoint(0, center)
            ?? controlPointFactory.createControlPoint(center);
        if (centerControlPoint === this.drawingPointTargets[0]) {
            reusedControlPoints.add(centerControlPoint);
        } else {
            this.bindControlPointSource(
                centerControlPoint,
                this.resolveEllipseControlPointSource(0, center),
            );
        }
        const axisControlPoints = controlPointFactory.createAxisControlPoints({
            centerWorld: center,
            radiusX,
            radiusY,
            rotation,
        });
        const majorPositivePoint = this.resolveReusableEllipseControlPoint(
            1,
            axisControlPoints.majorPositive.getPosition(),
        );
        if (majorPositivePoint) {
            axisControlPoints.majorPositive = majorPositivePoint;
            reusedControlPoints.add(majorPositivePoint);
        } else {
            this.bindControlPointSource(
                axisControlPoints.majorPositive,
                this.resolveEllipseControlPointSource(1, axisControlPoints.majorPositive.getPosition()),
            );
        }
        const minorPositivePoint = this.resolveReusableEllipseControlPoint(
            2,
            axisControlPoints.minorPositive.getPosition(),
        );
        if (minorPositivePoint) {
            axisControlPoints.minorPositive = minorPositivePoint;
            reusedControlPoints.add(minorPositivePoint);
        } else {
            this.bindControlPointSource(
                axisControlPoints.minorPositive,
                this.resolveEllipseControlPointSource(2, axisControlPoints.minorPositive.getPosition()),
            );
        }


        const ellipse = new SketchEllipseObject({
            startPoint: axisControlPoints.majorPositive,
            endPoint: axisControlPoints.minorPositive,
            centerPoint: centerControlPoint,
            material,
            referencePlane: this.referencePlane,
            definition: {
                centerWorld: center.clone(),
                radiusX,
                radiusY,
                rotation,
                startAngle: 0,
                endAngle: 2 * Math.PI,
                clockwise: false,
                segments: 64,
            },
        });

        controlPointFactory.registerCardinalControlPoints(ellipse, {
            centerPoint: centerControlPoint,
            axisControlPoints,
        });
        const controlPoints = controlPointFactory.collectRegisteredControlPoints({
            centerPoint: centerControlPoint,
            axisControlPoints,
        });

        ellipse.updateEllipse();
        if (this.viewer.shadedSketchContoursEnabled) {
            ellipse.syncContour();
        }

        // 同步材质快照。
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(ellipse);
        controlPoints.forEach((point) => {
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(point);
        });

        // 将椭圆和所有控制点作为独立草图实体添加到场景，确保交互系统可发现并支持拖动留影。
        this.viewer.addSketchObject(ellipse);

        this.addNewEllipseControlPoints(controlPoints, reusedControlPoints);

        this.sketchDrawingManager.notifySketchEntityCreated(ellipse);

        if (this.ellipseOptions.addConstructionLines) {
            const constructionLines = this.buildEllipseConstructionLines(
                {
                    majorStartPoint: axisControlPoints.majorPositive,
                    majorEndPoint: axisControlPoints.majorNegative,
                    minorStartPoint: axisControlPoints.minorPositive,
                    minorEndPoint: axisControlPoints.minorNegative,
                },
            );
            constructionLines.forEach((constructionLine) => {
                this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(constructionLine);
                this.viewer.addSketchObject(constructionLine);
                this.sketchDrawingManager.notifySketchEntityCreated(constructionLine);
            });
        }

        // 与 viewer 既有行为保持一致，创建后同步参数面板数据。
        const round = (value: number): number => Number(value.toFixed(8));
        this.ellipseParameters = {
            center: center.toArray() as [number, number, number],
            center2d: [round(localCenter.x), round(localCenter.y)],
            radiusX: round(radiusX),
            radiusY: round(radiusY),
            rotation: round((rotation * 180) / Math.PI),
        };
        this.ellipseParametersSubject.next(this.ellipseParameters);
    }


    /**
     * 解析椭圆绘制中可记录为来源的捕捉点对象。
     *
     * 说明：
     * 1. 捕捉点必须与最终控制点位置仍在闭合容差内，避免绘制模式投影后误记来源；
     * 2. 完整椭圆记录中心、长轴正端点、短轴正端点来源；
     * 3. 部分椭圆记录中心、长轴正端点、短轴正端点/弧起点与弧终点来源。
     *
     * @param pointIndex 绘制点击索引。
     * @param expectedPoint 最终控制点位置。
     * @returns 可记录的来源点对象；不可记录时返回 null。
     */
    private resolveEllipseControlPointSource(
        pointIndex: number,
        expectedPoint: THREE.Vector3 | null | undefined,
    ): SketchPointObject | null {
        const targetPoint = this.drawingPointTargets[pointIndex] ?? null;
        return this.resolveControlPointSource(targetPoint, expectedPoint);
    }

    /**
     * 解析椭圆绘制中是否命中了可直接复用的线段端点。
     * @param pointIndex 绘制点击索引。
     * @param expectedPoint 最终控制点位置。
     * @returns 可复用的线段端点；否则返回 null。
     */
    private resolveReusableEllipseControlPoint(
        pointIndex: number,
        expectedPoint: THREE.Vector3 | null | undefined,
    ): SketchPointObject | null {
        const targetPoint = this.drawingPointTargets[pointIndex] ?? null;
        return this.resolveReusableLineEndpointControlPoint(targetPoint, expectedPoint);
    }

    /**
     * 将椭圆新建控制点加入草图组。
     *
     * 说明：
     * 1. 所有绘制控制点都保持为新建点，捕捉点只作为来源引用；
     * 2. 控制点仍需进入草图组，保证选择、拖拽和点捕捉索引能发现它们；
     * 3. 通过 UUID 去重，兼容同一个控制点同时承担弧端点与轴标记点的情况。
     *
     * @param controlPoints 椭圆控制点列表。
     */
    private addNewEllipseControlPoints(
        controlPoints: readonly SketchPointObject[],
        reusedControlPoints: ReadonlySet<SketchPointObject> = new Set<SketchPointObject>(),
    ): void {
        const reusedPointIds = new Set(Array.from(reusedControlPoints).map((point) => point.uuid));
        const addedPointIds = new Set<string>();
        controlPoints.forEach((point) => {
            if (reusedPointIds.has(point.uuid) || addedPointIds.has(point.uuid)) {
                return;
            }

            this.viewer.addSketchObject(point);
            addedPointIds.add(point.uuid);
        });
    }


    /**
     * 在处理器内创建抛物线实体。

     */
    private createParabolaEntity(): void {
        if (this.drawingPoints.length < 4 ) {
            return;
        }

        const focus = this.drawingPoints[0];
        const vertex = this.drawingPoints[1];
        const startHint = this.drawingPoints[2];
        const endHint = this.drawingPoints[3];
        const definition = this.resolveParabolaDefinition(vertex, focus);
        if (!definition) {
            return;
        }

        const startData = this.projectPointToParabola(startHint, definition);
        const endData = this.projectPointToParabola(endHint, definition);
        const isConstruction = this.ellipseOptions.forConstruction;
        const material = this.createSketchLineMaterial(isConstruction, false);
        // 焦点与顶点：吸附命中已有端点时直接复用该点对象，确保与既有实体拓扑共点；
        // 起终点与焦点/顶点采用相同的复用判定：起终点会被投影回抛物线，投影后位置与吸附点
        // 仍在闭合容差内时同样复用该点对象，否则才新建（由 resolveReusableEllipseControlPoint 的容差检查自然排除）。
        const focalPoint = this.resolveReusableEllipseControlPoint(0, focus);
        const apexPoint = this.resolveReusableEllipseControlPoint(1, vertex);
        const parabolaStartPoint = this.resolveReusableEllipseControlPoint(2, startData.point);
        const parabolaEndPoint = this.resolveReusableEllipseControlPoint(3, endData.point);
        const reusedControlPoints = new Set<SketchPointObject>();
        if (focalPoint) {
            reusedControlPoints.add(focalPoint);
        }
        if (apexPoint) {
            reusedControlPoints.add(apexPoint);
        }
        if (parabolaStartPoint) {
            reusedControlPoints.add(parabolaStartPoint);
        }
        if (parabolaEndPoint) {
            reusedControlPoints.add(parabolaEndPoint);
        }
        const parabola = new SketchParabolaObject({
            referencePlane: this.referencePlane,
            material,
            isConstruction,
            focalPoint: focalPoint ?? undefined,
            apexPoint: apexPoint ?? undefined,
            startPoint: parabolaStartPoint ?? undefined,
            endPoint: parabolaEndPoint ?? undefined,
            definition: {
                focalWorld: focus.clone(),
                apexWorld: vertex.clone(),
                startParameter: startData.x,
                endParameter: endData.x,
                segments: 64,
            },
        });

        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(parabola);
        this.viewer.addSketchObject(parabola);
        parabola.getControlPoints().forEach((point) => {
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(point);
            // 复用点已是正式草图实体，禁止重复加入草图组。
            if (!reusedControlPoints.has(point)) {
                this.viewer.addSketchObject(point);
            }
        });
        this.sketchDrawingManager.notifySketchEntityCreated(parabola);

        const round = (value: number): number => Number(value.toFixed(8));
        const toPlane2d = (point: THREE.Vector3): [number, number] => {
            const local = this.referencePlane.worldToLocal(point);
            return [round(local.x), round(local.y)];
        };
        this.ellipseParameters = {
            anchor1: focus.toArray() as [number, number, number],
            anchor1_2d: toPlane2d(focus),
            anchor2: vertex.toArray() as [number, number, number],
            anchor2_2d: toPlane2d(vertex),
            start: startData.point.toArray() as [number, number, number],
            start2d: toPlane2d(startData.point),
            end: endData.point.toArray() as [number, number, number],
            end2d: toPlane2d(endData.point),
        };
        this.ellipseParametersSubject.next(this.ellipseParameters);
    }

    /**
     * 在处理器内创建部分椭圆实体。

     */
    private createPartialEllipseEntity(): void {
        if (this.drawingPoints.length < 4 ) {
            return;
        }

        const center = this.drawingPoints[0];
        const axisPoint1 = this.drawingPoints[1];
        const axisPoint2 = this.drawingPoints[2];
        const endHint = this.drawingPoints[3];
        const arcState = this.resolvePartialEllipseArcState(center, axisPoint1, axisPoint2, endHint, false);
        if (!arcState) {
            return;
        }

        const {localCenter, radiusX, radiusY, rotation} = arcState;
        const fixedStartPoint = arcState.startPoint;
        const endPoint = arcState.endPoint;

        const isConstruction = this.ellipseOptions.forConstruction;
        const material = this.createSketchLineMaterial(isConstruction, false);
        const controlPointFactory = new SketchEllipseControlPointFactory(this.referencePlane);

        const reusedControlPoints = new Set<SketchPointObject>();
        const centerControlPoint = this.resolveReusableEllipseControlPoint(0, center)
            ?? controlPointFactory.createControlPoint(center);
        if (centerControlPoint === this.drawingPointTargets[0]) {
            reusedControlPoints.add(centerControlPoint);
        } else {
            this.bindControlPointSource(
                centerControlPoint,
                this.resolveEllipseControlPointSource(0, center),
            );
        }
        const startControlPoint = this.resolveReusableEllipseControlPoint(2, fixedStartPoint)
            ?? controlPointFactory.createControlPoint(fixedStartPoint);
        if (startControlPoint === this.drawingPointTargets[2]) {
            reusedControlPoints.add(startControlPoint);
        } else {
            this.bindControlPointSource(
                startControlPoint,
                this.resolveEllipseControlPointSource(2, fixedStartPoint),
            );
        }
        const endControlPoint = this.resolveReusableEllipseControlPoint(3, endPoint)
            ?? controlPointFactory.createControlPoint(endPoint);
        if (endControlPoint === this.drawingPointTargets[3]) {
            reusedControlPoints.add(endControlPoint);
        } else {
            this.bindControlPointSource(
                endControlPoint,
                this.resolveEllipseControlPointSource(3, endPoint),
            );
        }
        const axisControlPoints = controlPointFactory.createAxisControlPoints({
            centerWorld: center,
            radiusX,
            radiusY,
            rotation,
        });
        const majorPositivePoint = this.resolveReusableEllipseControlPoint(
            1,
            axisControlPoints.majorPositive.getPosition(),
        );
        if (majorPositivePoint) {
            axisControlPoints.majorPositive = majorPositivePoint;
            reusedControlPoints.add(majorPositivePoint);
        } else {
            this.bindControlPointSource(
                axisControlPoints.majorPositive,
                this.resolveEllipseControlPointSource(1, axisControlPoints.majorPositive.getPosition()),
            );
        }
        const minorPositivePoint = this.resolveReusableEllipseControlPoint(
            2,
            axisControlPoints.minorPositive.getPosition(),
        );
        if (minorPositivePoint) {
            axisControlPoints.minorPositive = minorPositivePoint;
            reusedControlPoints.add(minorPositivePoint);
        } else {
            this.bindControlPointSource(
                axisControlPoints.minorPositive,
                this.resolveEllipseControlPointSource(2, axisControlPoints.minorPositive.getPosition()),
            );
        }

        const ellipse = new SketchEllipseObject({
            startPoint: startControlPoint,
            endPoint: endControlPoint,
            centerPoint: centerControlPoint,
            material,
            referencePlane: this.referencePlane,
            definition: {
                centerWorld: center.clone(),
                radiusX,
                radiusY,
                rotation,
                startAngle: arcState.startAngle,
                endAngle: arcState.endAngle,
                clockwise: arcState.clockwise,
                segments: 64,
            },
        });
        controlPointFactory.registerCardinalControlPoints(ellipse, {
            centerPoint: centerControlPoint,
            axisControlPoints,
        });
        const controlPoints = [
            startControlPoint,
            endControlPoint,
            ...controlPointFactory.collectRegisteredControlPoints({
                centerPoint: centerControlPoint,
                axisControlPoints,
            }),
        ];
        ellipse.updateEllipse();
        if (this.viewer.shadedSketchContoursEnabled) {
            ellipse.syncContour();
        }

        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(ellipse);
        controlPoints.forEach((point) => {
            this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(point);
        });

        this.viewer.addSketchObject(ellipse);

        this.addNewEllipseControlPoints(controlPoints, reusedControlPoints);

        this.sketchDrawingManager.notifySketchEntityCreated(ellipse);

        /**
         * 部分椭圆是开放椭圆弧，创建完成后必须像圆弧和开放样条一样进入统一混合闭合解析。
         * 完整椭圆走独立轮廓链路，不会进入当前部分椭圆创建方法，因此这里不会混淆轴控制点与弧端点。
         */
        this.tryFinalizeMixedClosedCollection([ellipse]);

        if (this.ellipseOptions.addConstructionLines) {
            const constructionLines = this.buildEllipseConstructionLines(
                {
                    majorStartPoint: axisControlPoints.majorPositive,
                    majorEndPoint: axisControlPoints.majorNegative,
                    minorStartPoint: axisControlPoints.minorPositive,
                    minorEndPoint: axisControlPoints.minorNegative,
                },
            );
            constructionLines.forEach((constructionLine) => {
                this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(constructionLine);
                this.viewer.addSketchObject(constructionLine);
                this.sketchDrawingManager.notifySketchEntityCreated(constructionLine);
            });
        }

        const round = (value: number): number => Number(value.toFixed(8));
        this.ellipseParameters = {
            center: center.toArray() as [number, number, number],
            center2d: [round(localCenter.x), round(localCenter.y)],
            radiusX: round(radiusX),
            radiusY: round(radiusY),
            rotation: round((rotation * 180) / Math.PI),
            start: fixedStartPoint.toArray() as [number, number, number],
            start2d: (() => {
                const local = this.referencePlane.worldToLocal(fixedStartPoint);
                return [round(local.x), round(local.y)];
            })(),
            end: endPoint.toArray() as [number, number, number],
            end2d: (() => {
                const local = this.referencePlane.worldToLocal(endPoint);
                return [round(local.x), round(local.y)];
            })(),
            startAngle: round((arcState.startAngle * 180) / Math.PI),
            endAngle: round((arcState.endAngle * 180) / Math.PI),
            arcAngle: round((arcState.arcAngle * 180) / Math.PI),
        };
        this.ellipseParametersSubject.next(this.ellipseParameters);
    }

    /**
     * 清空椭圆第一阶段长轴引导预览缓存引用。
     */
    private clearEllipseMajorAxisGuidePreviewCache(): void {
        this.ellipseMajorAxisGuideCirclePreview = null;
        this.ellipseMajorAxisGuideLinePreview = null;
    }


    /**
     * 清空椭圆第二阶段预览缓存引用。
     */
    private clearEllipseCurvePreviewCache(): void {
        this.ellipsePreviewCurve = null;
        this.ellipsePreviewIsConstruction = null;
        this.ellipsePreviewHasConstructionLines = null;
        this.ellipseMajorConstructionLinePreview = null;
        this.ellipseMinorConstructionLinePreview = null;
    }


    /**
     * 清空抛物线预览缓存引用。
     * 不负责 dispose，资源生命周期由 previewCircle、previewLine → clearDrawingPreview 统一管理。
     */
    private clearParabolaPreviewCache(): void {
        this.parabolaPreviewCurve = null;
        this.parabolaDefinitionGuideLinePreview = null;
    }


    /**
     * 判断椭圆第一阶段长轴引导预览是否仍由绘制管理器持有。
     * @returns 当前缓存对象是否仍可直接复用。
     */
    private isEllipseMajorAxisGuidePreviewActive(): boolean {
        return Boolean(
            this.ellipseMajorAxisGuideCirclePreview
            && this.ellipseMajorAxisGuideLinePreview
            && this.sketchDrawingManager.previewCircle === this.ellipseMajorAxisGuideCirclePreview
            && this.sketchDrawingManager.previewLine === this.ellipseMajorAxisGuideLinePreview,
        );
    }

    /**
     * 创建椭圆第一阶段长轴引导预览。
     * 说明：
     * 1. SolidWorks 椭圆第一点后会以中心点到当前鼠标点作为长轴半径进行引导；
     * 2. 该阶段还没有短轴定义，不能提前进入最终椭圆实体预览；
     * 3. 第二点点击完成后仍交给 `createEllipsePreviewInHandler()` 执行原有完整椭圆预览逻辑。
     * @param center 椭圆中心世界坐标。
     * @param currentPoint 当前鼠标世界坐标。
     */
    private createEllipseMajorAxisGuidePreview(center: THREE.Vector3, currentPoint: THREE.Vector3): void {
        const localCenter = this.referencePlane.worldToLocalTo(center, this.ellipseMajorAxisGuideLocalCenter);
        const localCurrent = this.referencePlane.worldToLocalTo(currentPoint, this.ellipseMajorAxisGuideLocalCurrent);
        const radius = localCenter.distanceTo(localCurrent);
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            this.destroyPreview();
            return;
        }

        // 第一点后的外轮廓只是长轴半径引导，使用圆形虚线避免误表达为最终椭圆。
        const guideCirclePoints = this.updateEllipseMajorAxisGuideCirclePoints(localCenter, radius);
        const guideLinePoints = this.updateEllipseMajorAxisGuideLinePoints(center, localCurrent);

        if (!this.isEllipseMajorAxisGuidePreviewActive()) {
            this.destroyPreview();
            this.sketchDrawingManager.previewCircle = this.createScreenSpaceDashedPreviewLine(
                guideCirclePoints,
                '椭圆第一阶段外轮廓预览-屏幕空间虚线材质',
            );
            this.ellipseMajorAxisGuideCirclePreview = this.sketchDrawingManager.previewCircle;
            this.viewer.addObject(this.sketchDrawingManager.previewCircle);

            // 半径线用于表达当前长轴方向，必须跟随鼠标投影到草图平面后的点位。
            this.sketchDrawingManager.previewLine = this.createScreenSpaceDashedPreviewLine(
                guideLinePoints,
                '椭圆第一阶段半径引导预览-屏幕空间虚线材质',
            );
            this.ellipseMajorAxisGuideLinePreview = this.sketchDrawingManager.previewLine;
            this.viewer.addObject(this.sketchDrawingManager.previewLine);
            return;
        }

        // 鼠标移动属于高频路径，只刷新已有几何，避免重复创建线对象、材质和 GPU 资源。
        this.updateScreenSpaceDashedPreviewLine(
            this.ellipseMajorAxisGuideCirclePreview as THREE.Line,
            guideCirclePoints,
        );
        this.updateScreenSpaceDashedPreviewLine(
            this.ellipseMajorAxisGuideLinePreview as THREE.Line,
            guideLinePoints,
        );
    }

    /**
     * 原地更新椭圆第一阶段长轴引导外轮廓采样点。
     * @param localCenter 椭圆中心局部坐标。
     * @param radius 当前长轴半径。
     * @returns 可直接写入预览几何的复用采样点数组。
     */
    private updateEllipseMajorAxisGuideCirclePoints(
        localCenter: THREE.Vector2,
        radius: number,
    ): THREE.Vector3[] {
        for (let index = 0; index <= ELLIPSE_MAJOR_AXIS_GUIDE_SEGMENTS; index += 1) {
            const angle = (index / ELLIPSE_MAJOR_AXIS_GUIDE_SEGMENTS) * Math.PI * 2;
            const localX = localCenter.x + radius * Math.cos(angle);
            const localY = localCenter.y + radius * Math.sin(angle);
            // 高频预览路径直接写入复用 Vector3，避免每次鼠标移动重新分配整圈采样点。
            this.referencePlane.localToWorldTo(localX, localY, this.ellipseMajorAxisGuideCirclePoints[index]);
        }

        return this.ellipseMajorAxisGuideCirclePoints;
    }

    /**
     * 原地更新椭圆第一阶段长轴半径引导线采样点。
     * @param center 椭圆中心世界坐标。
     * @param localCurrent 当前鼠标投影到草图平面后的局部坐标。
     * @returns 可直接写入预览几何的复用采样点数组。
     */
    private updateEllipseMajorAxisGuideLinePoints(
        center: THREE.Vector3,
        localCurrent: THREE.Vector2,
    ): THREE.Vector3[] {
        this.ellipseMajorAxisGuideLinePoints[0].copy(center);
        this.referencePlane.localToWorldTo(localCurrent, this.ellipseMajorAxisGuideLinePoints[1]);
        return this.ellipseMajorAxisGuideLinePoints;
    }

    /**
     * 原地更新椭圆预览线几何。
     * 说明：同一阶段的点数固定时直接改写 position attribute，减少高频鼠标移动中的对象分配和材质重建。
     * @param line 待更新的预览线。
     * @param points 新折线点列。
     */
    private updateEllipsePreviewLineGeometry(line: THREE.Line, points: THREE.Vector3[]): void {
        const positionAttribute = line.geometry.getAttribute('position');
        if (positionAttribute instanceof THREE.BufferAttribute && positionAttribute.count === points.length) {
            points.forEach((point, index) => {
                positionAttribute.setXYZ(index, point.x, point.y, point.z);
            });
            positionAttribute.needsUpdate = true;
        } else {
            line.geometry.setFromPoints(points);
        }

        line.geometry.computeBoundingSphere();
    }

    /**
     * 更新屏幕空间虚线预览对象的几何。
     * @param line 待更新的预览线。
     * @param points 新折线点列。
     */
    private updateScreenSpaceDashedPreviewLine(line: THREE.Line, points: THREE.Vector3[]): void {
        this.updateEllipsePreviewLineGeometry(line, points);
        syncScreenSpaceDashedLineGeometry(line);
    }

    /** 在处理器内创建完整椭圆预览。 */
    private createEllipsePreviewInHandler(
        center: THREE.Vector3,
        axisPoint1: THREE.Vector3,
        axisPoint2: THREE.Vector3,
    ): void {
        const definition = this.resolveEllipseDefinition(center, axisPoint1, axisPoint2);
        if (!definition) {
            this.destroyPreview();
            return;
        }
        const points = this.buildEllipsePolyline(
            definition.localCenter,
            definition.radiusX,
            definition.radiusY,
            definition.rotation,
            0,
            Math.PI * 2,
            false,
            128,
        );

        this.renderEllipseCurvePreview(
            points,
            definition.localCenter,
            definition.radiusX,
            definition.radiusY,
            definition.rotation,
            this.ellipseOptions.forConstruction,
        );

    }


    /** 在处理器内创建部分椭圆弧预览。 */
    private createPartialEllipseArcPreviewInHandler(
        center: THREE.Vector3,
        axisPoint1: THREE.Vector3,
        axisPoint2: THREE.Vector3,
        endPoint: THREE.Vector3,
    ): void {
        const arcState = this.resolvePartialEllipseArcState(center, axisPoint1, axisPoint2, endPoint, true);
        if (!arcState) {
            this.destroyPreview();
            return;
        }
        const points = this.buildEllipsePolyline(
            arcState.localCenter,
            arcState.radiusX,
            arcState.radiusY,
            arcState.rotation,
            arcState.startAngle,
            arcState.endAngle,
            arcState.clockwise,
            96,
        );

        this.renderEllipseCurvePreview(
            points,
            arcState.localCenter,
            arcState.radiusX,
            arcState.radiusY,
            arcState.rotation,
            this.ellipseOptions.forConstruction,
        );

    }


    /**
     * 判断抛物线第一阶段定义引导是否仍由绘制管理器完整持有。
     * @returns 临时抛物线和焦点引导线是否都可原位更新。
     */
    private isParabolaDefinitionGuidePreviewActive(): boolean {
        return Boolean(
            this.parabolaPreviewCurve
            && this.parabolaDefinitionGuideLinePreview
            && this.sketchDrawingManager.previewCircle === this.parabolaPreviewCurve
            && this.sketchDrawingManager.previewLine === this.parabolaDefinitionGuideLinePreview,
        );
    }

    /**
     * 更新抛物线第一阶段焦点引导线的端点。
     * @param focus 已选定的抛物线焦点。
     * @param temporaryVertex 当前鼠标位置表达的临时顶点。
     * @returns 可直接写入预览几何的复用点列。
     */
    private updateParabolaDefinitionGuideLinePoints(
        focus: THREE.Vector3,
        temporaryVertex: THREE.Vector3,
    ): THREE.Vector3[] {
        this.parabolaDefinitionGuideLinePoints[0].copy(focus);
        this.parabolaDefinitionGuideLinePoints[1].copy(temporaryVertex);
        return this.parabolaDefinitionGuideLinePoints;
    }

    /**
     * 创建抛物线第一阶段定义引导预览。
     * 说明：
     * 1. 第一次点击确定焦点，当前鼠标点作为尚未提交的临时顶点；
     * 2. 临时焦点与顶点已经足以定义抛物线，因此同步显示对称虚线弧和虚线焦距引导；
     * 3. 连续鼠标移动只更新既有对象，第二次点击后由后续阶段统一替换预览。
     * @param focus 已选定的抛物线焦点。
     * @param temporaryVertex 当前鼠标位置表达的临时顶点。
     */
    private createParabolaDefinitionGuidePreview(
        focus: THREE.Vector3,
        temporaryVertex: THREE.Vector3,
    ): void {
        const definition = this.resolveParabolaDefinition(temporaryVertex, focus);
        if (!definition) {
            this.destroyPreview();
            return;
        }

        // 第一阶段只表达当前抛物线定义，使用有限对称范围避免焦距增大时预览无限发散。
        const extent = definition.p * PARABOLA_PREVIEW_EXTENT_FACTOR;
        const curvePoints = this.buildParabolaPoints(
            definition,
            -extent,
            extent,
            PARABOLA_PREVIEW_SEGMENTS,
        );
        const guideLinePoints = this.updateParabolaDefinitionGuideLinePoints(focus, temporaryVertex);

        if (!this.isParabolaDefinitionGuidePreviewActive()) {
            this.destroyPreview();
            this.sketchDrawingManager.previewCircle = this.createScreenSpaceDashedPreviewLine(
                curvePoints,
                '抛物线第一阶段弧线预览-屏幕空间虚线材质',
            );
            this.parabolaPreviewCurve = this.sketchDrawingManager.previewCircle;
            this.viewer.addObject(this.sketchDrawingManager.previewCircle);

            this.sketchDrawingManager.previewLine = this.createScreenSpaceDashedPreviewLine(
                guideLinePoints,
                '抛物线第一阶段焦点引导预览-屏幕空间虚线材质',
            );
            this.parabolaDefinitionGuideLinePreview = this.sketchDrawingManager.previewLine;
            this.viewer.addObject(this.sketchDrawingManager.previewLine);
            return;
        }

        // 两种预览的采样拓扑固定，原位刷新可避免鼠标移动时反复分配 GPU 几何和材质。
        this.updateScreenSpaceDashedPreviewLine(this.parabolaPreviewCurve as THREE.Line, curvePoints);
        this.updateScreenSpaceDashedPreviewLine(
            this.parabolaDefinitionGuideLinePreview as THREE.Line,
            guideLinePoints,
        );
    }


    /**
     * 在处理器内创建抛物线第二阶段预览（焦点+顶点已选定）。
     *
     * SolidWorks 行为：选定焦点和顶点后，预览显示以顶点为中心、
     * 对称展开的虚线弧段，范围由鼠标位置决定但有上限防止发散。
     */
    private createParabolaPreviewInHandler(
        focus: THREE.Vector3,
        vertex: THREE.Vector3,
        hintPoint: THREE.Vector3,
    ): void {
        const def = this.resolveParabolaDefinition(vertex, focus);
        if (!def) {
            this.destroyPreview();
            return;
        }

        // 预览范围根据焦距 p 动态计算（p = 焦点到顶点距离，即抛物线开口大小）
        const extent = def.p * PARABOLA_PREVIEW_EXTENT_FACTOR;

        // 以顶点为中心对称展开，两边都可自由选择起点（对齐 SolidWorks 交互）
        const points = this.buildParabolaPoints(def, -extent, extent, PARABOLA_PREVIEW_SEGMENTS);

        this.renderPreviewPolyline(points);
    }


    /**
     * 在处理器内创建抛物线段第三阶段预览（起点提示已选定）。
     *
     * SolidWorks 行为：范围由用户已选定的起终点自然约束，不会发散；
     * 预览线型为虚线，与最终实体样式正交。
     */
    private createParabolaSegmentPreviewInHandler(
        focus: THREE.Vector3,
        vertex: THREE.Vector3,
        startHint: THREE.Vector3,
        endHint: THREE.Vector3,
    ): void {
        const def = this.resolveParabolaDefinition(vertex, focus);
        if (!def) {
            this.destroyPreview();
            return;
        }
        const startData = this.projectPointToParabola(startHint, def);
        const endData = this.projectPointToParabola(endHint, def);
        const points = this.buildParabolaPoints(
            def,
            startData.x,
            endData.x,
            PARABOLA_PREVIEW_SEGMENTS,
        );
        this.renderPreviewPolyline(points);
    }


    /** 构建椭圆折线。 */
    private buildEllipsePolyline(
        localCenter: THREE.Vector2,
        radiusX: number,
        radiusY: number,
        rotation: number,
        startAngle: number,
        endAngle: number,
        clockwise: boolean,
        segments: number,
    ): THREE.Vector3[] {
        const points: THREE.Vector3[] = [];
        let from = startAngle;
        let to = endAngle;
        if (clockwise && from < to) {
            from += Math.PI * 2;
        } else if (!clockwise && to < from) {
            to += Math.PI * 2;
        }
        for (let i = 0; i <= segments; i += 1) {
            const t = i / segments;
            const angle = clockwise
                ? from - (from - to) * t
                : from + (to - from) * t;
            points.push(
                this.getEllipseWorldPointFromAngle(
                    angle,
                    localCenter,
                    radiusX,
                    radiusY,
                    rotation,
                ),
            );
        }
        return points;
    }

    /**
     * 渲染抛物线折线预览。
     *
     * 说明：使用 ScreenSpaceDashedLineNodeMaterial 替代 LineDashedMaterial。
     * LineDashedMaterial 依赖 GLSL 着色器的 USE_DASH define，在 WebGPU 后端
     * （three/webgpu）下虚线退化实线。ScreenSpaceDashedLineNodeMaterial 基于
     * Node Material 体系，在 WebGPU 下正确渲染屏幕空间虚线。
     * 参照椭圆构造线预览的「首次创建→后续几何更新 + sync」复用模式。
     *
     * @param points 折线采样点。
     */
    private renderPreviewPolyline(points: THREE.Vector3[]): void {
        if (points.length < 2) {
            this.destroyPreview();
            return;
        }

        // 高频复用路径：只有纯弧线阶段才直接复用外轮廓；若第一阶段引导线仍在，必须整体切换预览状态。
        if (
            this.parabolaPreviewCurve
            && this.sketchDrawingManager.previewCircle === this.parabolaPreviewCurve
            && this.parabolaDefinitionGuideLinePreview === null
            && this.sketchDrawingManager.previewLine === null
        ) {
            this.updateScreenSpaceDashedPreviewLine(this.parabolaPreviewCurve, points);
            return;
        }

        // 首次创建或阶段切换时统一清理旧预览，确保焦点引导线不会残留到起终点选择阶段。
        this.destroyPreview();
        const line = this.createScreenSpaceDashedPreviewLine(
            points,
            '抛物线弧段预览-屏幕空间虚线材质',
        );

        this.sketchDrawingManager.previewCircle = line;
        this.parabolaPreviewCurve = line;
        this.viewer.addObject(line);
    }

}
